/*
  Shared predictive-search controller.
  One small controller powers every general storefront search field so the
  homepage hero, header, mobile header, drawer, and search page all behave the
  same way without duplicating request logic.
*/
(function () {
  var SEARCH_MIN_LENGTH = 2;
  var SEARCH_DEBOUNCE_MS = 180;
  var searchInstances = [];
  var instanceCounter = 0;

  function debounce(callback, wait) {
    var timerId = null;

    return function () {
      var args = arguments;
      var context = this;

      window.clearTimeout(timerId);
      timerId = window.setTimeout(function () {
        callback.apply(context, args);
      }, wait);
    };
  }

  function parseResponseMarkup(responseText) {
    var parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
    return parsedDocument.querySelector('#shopify-section-predictive-search [data-predictive-search-response]');
  }

  function buildPredictiveUrl(query) {
    var localeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    var searchParams = new URLSearchParams();

    searchParams.set('q', query);
    searchParams.set('resources[type]', 'product');
    searchParams.set('resources[limit]', '6');
    searchParams.set('resources[options][unavailable_products]', 'last');
    searchParams.set('section_id', 'predictive-search');

    return localeRoot + 'search/suggest?' + searchParams.toString();
  }

  function TCGHivePredictiveSearch(root) {
    this.root = root;
    this.context = root.getAttribute('data-search-context') || '';
    this.form = root.querySelector('[data-search-form]');
    this.input = root.querySelector('[data-search-input]');
    this.panel = root.querySelector('[data-search-panel]');
    this.results = root.querySelector('[data-predictive-results]');
    this.staticPanel = root.querySelector('[data-search-static-panel]');
    this.clearButton = root.querySelector('[data-search-clear]');
    this.abortController = null;
    this.requestToken = 0;
    this.active = false;
    this.handleDebouncedInput = debounce(this.handleInput.bind(this), SEARCH_DEBOUNCE_MS);

    if (!this.form || !this.input || !this.panel || !this.results) {
      return;
    }

    this.ensureAccessibility();
    this.bindEvents();
    this.updateClearButton();
  }

  TCGHivePredictiveSearch.prototype.ensureAccessibility = function () {
    var resultsId = this.results.id;

    if (!resultsId) {
      instanceCounter += 1;
      resultsId = 'TcghivePredictiveResults-' + instanceCounter;
      this.results.id = resultsId;
    }

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', resultsId);
    this.input.setAttribute('aria-haspopup', 'listbox');
  };

  TCGHivePredictiveSearch.prototype.bindEvents = function () {
    var self = this;

    this.input.addEventListener('input', function () {
      self.updateClearButton();
      self.handleDebouncedInput();
    });

    this.input.addEventListener('focus', function () {
      self.handleFocus();
    });

    this.input.addEventListener('keydown', function (event) {
      self.handleKeydown(event);
    });

    this.form.addEventListener('submit', function (event) {
      self.handleSubmit(event);
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('pointerdown', function (event) {
        /*
          Prevent the input from blurring before the clear action runs. This
          keeps the search instance stable and stops near-edge clicks from
          turning into inconsistent form interactions.
        */
        event.preventDefault();
      });

      this.clearButton.addEventListener('click', function (event) {
        /*
          The clear control lives inside a real search form, so its interaction
          must never bubble into a form submission or an outer click handler.
        */
        event.preventDefault();
        event.stopPropagation();
        self.clear();
      });
    }
  };

  TCGHivePredictiveSearch.prototype.setExpanded = function (expanded) {
    this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  TCGHivePredictiveSearch.prototype.updateClearButton = function () {
    var hasValue = this.input.value.trim() !== '';

    if (!this.clearButton) {
      return;
    }

    this.clearButton.hidden = !hasValue;
  };

  TCGHivePredictiveSearch.prototype.abortPendingRequest = function () {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  };

  TCGHivePredictiveSearch.prototype.openStaticPanel = function () {
    if (!this.staticPanel) {
      this.close();
      return;
    }

    this.panel.hidden = false;
    this.staticPanel.hidden = false;
    this.results.hidden = true;
    this.active = true;
    this.setExpanded(true);
  };

  TCGHivePredictiveSearch.prototype.openResultsPanel = function () {
    this.panel.hidden = false;

    if (this.staticPanel) {
      this.staticPanel.hidden = true;
    }

    this.results.hidden = false;
    this.active = true;
    this.setExpanded(true);
  };

  TCGHivePredictiveSearch.prototype.close = function () {
    this.abortPendingRequest();
    this.active = false;
    this.panel.hidden = true;

    if (this.staticPanel) {
      this.staticPanel.hidden = false;
    }

    this.results.hidden = true;
    this.setExpanded(false);
  };

  TCGHivePredictiveSearch.prototype.clear = function () {
    /*
      Increment the request token before aborting so a stale predictive
      response cannot repopulate the panel after the customer has cleared the
      query.
    */
    this.requestToken += 1;
    this.input.value = '';
    this.results.innerHTML = '';
    this.updateClearButton();
    this.abortPendingRequest();

    if (this.staticPanel) {
      this.openStaticPanel();
    } else {
      this.close();
    }

    this.input.focus({ preventScroll: true });
  };

  TCGHivePredictiveSearch.prototype.handleFocus = function () {
    var query = this.input.value.trim();

    if (query.length >= SEARCH_MIN_LENGTH && this.results.innerHTML.trim() !== '') {
      this.openResultsPanel();
      return;
    }

    if (query.length < SEARCH_MIN_LENGTH && this.staticPanel) {
      this.openStaticPanel();
    }
  };

  TCGHivePredictiveSearch.prototype.handleInput = function () {
    var query = this.input.value.trim();

    if (query.length < SEARCH_MIN_LENGTH) {
      this.abortPendingRequest();
      this.results.innerHTML = '';

      if (this.staticPanel && document.activeElement === this.input) {
        this.openStaticPanel();
      } else {
        this.close();
      }

      return;
    }

    this.fetchResults(query);
  };

  TCGHivePredictiveSearch.prototype.fetchResults = function (query) {
    var self = this;
    var nextToken;
    var requestUrl;

    this.abortPendingRequest();
    this.abortController = new AbortController();
    this.requestToken += 1;
    nextToken = this.requestToken;
    requestUrl = buildPredictiveUrl(query);

    this.panel.hidden = false;

    fetch(requestUrl, {
      signal: this.abortController.signal,
      headers: {
        Accept: 'text/html'
      }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('predictive-search');
        }

        return response.text();
      })
      .then(function (responseText) {
        var responseMarkup;

        if (nextToken !== self.requestToken) {
          return;
        }

        responseMarkup = parseResponseMarkup(responseText);

        if (!responseMarkup) {
          self.results.innerHTML = '';
          self.close();
          return;
        }

        self.results.innerHTML = responseMarkup.innerHTML;
        self.openResultsPanel();
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') {
          return;
        }

        self.results.innerHTML = '';

        if (self.staticPanel && self.input.value.trim().length < SEARCH_MIN_LENGTH) {
          self.openStaticPanel();
        } else {
          self.close();
        }

        /*
          Predictive search is progressive enhancement only. A failed request
          should not block the normal search form submission.
        */
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn('TCGHive predictive search request failed.');
        }
      });
  };

  TCGHivePredictiveSearch.prototype.getViewAllDestination = function (query, container) {
    var searchScope = container || this.results;
    var viewAllLink = searchScope ? searchScope.querySelector('[data-predictive-view-all]') : null;
    var viewAllQuery;

    if (!viewAllLink) {
      return '';
    }

    viewAllQuery = (viewAllLink.getAttribute('data-predictive-query') || '').trim();

    if (viewAllQuery.toLowerCase() !== query.toLowerCase()) {
      return '';
    }

    return viewAllLink.href || '';
  };

  TCGHivePredictiveSearch.prototype.submitWithoutResolver = function (query) {
    this.input.value = query;
    this.form.submit();
  };

  TCGHivePredictiveSearch.prototype.resolveAndSubmit = function (query) {
    var self = this;
    var requestUrl = buildPredictiveUrl(query);

    this.abortPendingRequest();

    fetch(requestUrl, {
      headers: {
        Accept: 'text/html'
      }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('predictive-submit');
        }

        return response.text();
      })
      .then(function (responseText) {
        var responseMarkup = parseResponseMarkup(responseText);
        var destination = self.getViewAllDestination(query, responseMarkup);

        if (destination) {
          window.location.href = destination;
          return;
        }

        self.submitWithoutResolver(query);
      })
      .catch(function () {
        self.submitWithoutResolver(query);
      });
  };

  TCGHivePredictiveSearch.prototype.handleKeydown = function (event) {
    var firstResultLink;
    var resultLinks;
    var currentIndex = -1;
    var direction = 0;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'Enter') {
      if (this.input.value.trim() === '') {
        event.preventDefault();
        this.input.focus();
      }

      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    if (this.results.hidden) {
      return;
    }

    resultLinks = Array.prototype.slice.call(this.results.querySelectorAll('.tcghive-predictive-result'));

    if (!resultLinks.length) {
      return;
    }

    event.preventDefault();
    direction = event.key === 'ArrowDown' ? 1 : -1;
    currentIndex = resultLinks.indexOf(document.activeElement);

    if (currentIndex === -1) {
      if (direction === 1) {
        firstResultLink = resultLinks[0];
      } else {
        firstResultLink = resultLinks[resultLinks.length - 1];
      }

      if (firstResultLink) {
        firstResultLink.focus();
      }

      return;
    }

    currentIndex += direction;

    if (currentIndex < 0) {
      this.input.focus();
      return;
    }

    if (currentIndex >= resultLinks.length) {
      currentIndex = resultLinks.length - 1;
    }

    if (resultLinks[currentIndex]) {
      resultLinks[currentIndex].focus();
    }
  };

  TCGHivePredictiveSearch.prototype.handleSubmit = function (event) {
    var trimmedQuery = this.input.value.trim();
    var currentDestination;

    if (trimmedQuery === '') {
      event.preventDefault();
      this.input.focus();
      return;
    }

    currentDestination = this.getViewAllDestination(trimmedQuery);

    if (currentDestination) {
      event.preventDefault();
      window.location.href = currentDestination;
      return;
    }

    this.input.value = trimmedQuery;

    if (trimmedQuery.length >= SEARCH_MIN_LENGTH) {
      /*
        Ask the same server-rendered predictive section for the final "View
        all" route. This keeps submit/Enter aligned with the visible footer
        action without maintaining a second filter map in browser JavaScript.
      */
      event.preventDefault();
      this.resolveAndSubmit(trimmedQuery);
    }
  };

  function initializePredictiveSearch() {
    searchInstances = Array.prototype.slice.call(document.querySelectorAll('[data-tcghive-search]'))
      .map(function (searchRoot) {
        return new TCGHivePredictiveSearch(searchRoot);
      })
      .filter(function (instance) {
        return instance && instance.input;
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initializePredictiveSearch();

    document.addEventListener('click', function (event) {
      searchInstances.forEach(function (instance) {
        if (!instance.root.contains(event.target)) {
          instance.close();
        }
      });
    });

    document.addEventListener('focusin', function (event) {
      searchInstances.forEach(function (instance) {
        if (!instance.root.contains(event.target) && !instance.root.contains(document.activeElement)) {
          instance.close();
        }
      });
    });
  });

  document.addEventListener('shopify:section:load', function () {
    initializePredictiveSearch();
  });
})();
